import { ChunkingService } from './rag/chunkingService';
import { EmbeddingService } from './rag/embeddingService';
import { VectorStoreService } from './rag/vectorStoreService';

export interface LearningModule {
  id: string;
  number: number;
  title: string;
  description: string;
  content: string;
  duration: string;
  level: 'Beginner' | 'Intermediate' | 'Advanced';
  filepath: string;
}

export interface ResearchDocument {
  id: string;
  title: string;
  description: string;
  content: string;
  category: string;
  filepath: string;
}

export interface PromptExample {
  id: string;
  title: string;
  description: string;
  prompt: string;
  category: string;
  content: string; // Full context including example
}

export interface ProcessingProgress {
  stage: 'loading' | 'chunking' | 'embedding' | 'storing' | 'completed' | 'error';
  progress: number;
  message?: string;
}

export class ContentService {
  private static instance: ContentService;
  private chunkingService: ChunkingService;
  private embeddingService: EmbeddingService;
  private vectorStore: VectorStoreService;

  private constructor() {
    this.chunkingService = ChunkingService.getInstance();
    this.embeddingService = EmbeddingService.getInstance();
    this.vectorStore = VectorStoreService.getInstance();
  }

  public static getInstance(): ContentService {
    if (!ContentService.instance) {
      ContentService.instance = new ContentService();
    }
    return ContentService.instance;
  }

  /**
   * Process a learning module through the RAG pipeline
   */
  public async processModule(
    module: LearningModule,
    onProgress?: (progress: ProcessingProgress) => void
  ): Promise<void> {
    try {
      // Stage 1: Load content
      onProgress?.({ stage: 'loading', progress: 10, message: 'Loading module content...' });

      // Content is already loaded in the module object
      if (!module.content || module.content.trim().length === 0) {
        throw new Error('Module content is empty');
      }

      // Stage 2: Chunk the content
      onProgress?.({ stage: 'chunking', progress: 30, message: 'Breaking content into chunks...' });

      const chunks = await this.chunkingService.createSemanticChunks(
        module.id,
        module.title,
        module.content,
        {
          chunkSize: 1000,
          chunkOverlap: 200
        }
      );

      console.log(`Created ${chunks.length} chunks for ${module.title}`);

      // Stage 3: Generate embeddings
      onProgress?.({ stage: 'embedding', progress: 50, message: 'Generating embeddings...' });

      const embeddings = await this.embeddingService.generateChunkEmbeddings(chunks);

      console.log(`Generated ${embeddings.size} embeddings`);

      // Stage 4: Store in vector database
      onProgress?.({ stage: 'storing', progress: 80, message: 'Storing in database...' });

      // Store document metadata
      const storedDoc = await this.vectorStore.storeDocument({
        name: module.title,
        type: 'learning-module',
        userId: 'local-dev', // For local development
        processingStatus: 'processing',
        metadata: {
          number: module.number,
          description: module.description,
          duration: module.duration,
          level: module.level,
          filepath: module.filepath
        }
      });

      // Store chunks with embeddings
      await this.vectorStore.storeChunks(storedDoc.id, chunks, embeddings);

      // Update document status
      await this.vectorStore.updateDocumentStatus(storedDoc.id, 'completed');

      // Stage 5: Complete
      onProgress?.({ stage: 'completed', progress: 100, message: 'Processing complete!' });

      console.log(`✅ Successfully processed module: ${module.title}`);
    } catch (error) {
      console.error('Error processing module:', error);
      onProgress?.({
        stage: 'error',
        progress: 0,
        message: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  /**
   * Process a research document through the RAG pipeline
   */
  public async processResearchDocument(
    document: ResearchDocument,
    onProgress?: (progress: ProcessingProgress) => void
  ): Promise<void> {
    try {
      onProgress?.({ stage: 'loading', progress: 10, message: 'Loading research document...' });

      if (!document.content || document.content.trim().length === 0) {
        throw new Error('Document content is empty');
      }

      onProgress?.({ stage: 'chunking', progress: 30, message: 'Breaking content into chunks...' });

      const chunks = await this.chunkingService.createSemanticChunks(
        document.id,
        document.title,
        document.content,
        {
          chunkSize: 1500, // Larger chunks for research documents
          chunkOverlap: 300
        }
      );

      console.log(`Created ${chunks.length} chunks for research: ${document.title}`);

      onProgress?.({ stage: 'embedding', progress: 50, message: 'Generating embeddings...' });

      const embeddings = await this.embeddingService.generateChunkEmbeddings(chunks);

      onProgress?.({ stage: 'storing', progress: 80, message: 'Storing in database...' });

      const storedDoc = await this.vectorStore.storeDocument({
        name: document.title,
        type: 'research-document',
        userId: 'local-dev',
        processingStatus: 'processing',
        metadata: {
          description: document.description,
          category: document.category,
          filepath: document.filepath
        }
      });

      await this.vectorStore.storeChunks(storedDoc.id, chunks, embeddings);
      await this.vectorStore.updateDocumentStatus(storedDoc.id, 'completed');

      onProgress?.({ stage: 'completed', progress: 100, message: 'Processing complete!' });

      console.log(`✅ Successfully processed research: ${document.title}`);
    } catch (error) {
      console.error('Error processing research document:', error);
      onProgress?.({
        stage: 'error',
        progress: 0,
        message: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  /**
   * Process a prompt example through the RAG pipeline
   */
  public async processPromptExample(
    prompt: PromptExample,
    onProgress?: (progress: ProcessingProgress) => void
  ): Promise<void> {
    try {
      onProgress?.({ stage: 'loading', progress: 10, message: 'Loading prompt example...' });

      if (!prompt.content || prompt.content.trim().length === 0) {
        throw new Error('Prompt content is empty');
      }

      onProgress?.({ stage: 'chunking', progress: 30, message: 'Processing prompt...' });

      const chunks = await this.chunkingService.createSemanticChunks(
        prompt.id,
        prompt.title,
        prompt.content,
        {
          chunkSize: 800, // Smaller chunks for prompts
          chunkOverlap: 150
        }
      );

      console.log(`Created ${chunks.length} chunks for prompt: ${prompt.title}`);

      onProgress?.({ stage: 'embedding', progress: 50, message: 'Generating embeddings...' });

      const embeddings = await this.embeddingService.generateChunkEmbeddings(chunks);

      onProgress?.({ stage: 'storing', progress: 80, message: 'Storing in database...' });

      const storedDoc = await this.vectorStore.storeDocument({
        name: prompt.title,
        type: 'prompt-example',
        userId: 'local-dev',
        processingStatus: 'processing',
        metadata: {
          description: prompt.description,
          category: prompt.category,
          prompt: prompt.prompt
        }
      });

      await this.vectorStore.storeChunks(storedDoc.id, chunks, embeddings);
      await this.vectorStore.updateDocumentStatus(storedDoc.id, 'completed');

      onProgress?.({ stage: 'completed', progress: 100, message: 'Processing complete!' });

      console.log(`✅ Successfully processed prompt: ${prompt.title}`);
    } catch (error) {
      console.error('Error processing prompt example:', error);
      onProgress?.({
        stage: 'error',
        progress: 0,
        message: error instanceof Error ? error.message : 'Unknown error'
      });
      throw error;
    }
  }

  /**
   * Load a module from markdown file
   */
  public async loadModuleFromFile(filepath: string): Promise<string> {
    try {
      const response = await fetch(filepath);
      if (!response.ok) {
        throw new Error(`Failed to load module: ${response.statusText}`);
      }
      return await response.text();
    } catch (error) {
      console.error('Error loading module file:', error);
      throw error;
    }
  }

  /**
   * Get all processed modules from vector store
   */
  public async getProcessedModules(): Promise<any[]> {
    return await this.vectorStore.getAllDocuments();
  }

  /**
   * Search for content using natural language query
   */
  public async searchContent(query: string, topK: number = 5) {
    try {
      // Generate query embedding
      const queryEmbedding = await this.embeddingService.generateQueryEmbedding(query);

      // Search vector store
      const results = await this.vectorStore.searchSimilarChunks(queryEmbedding, {
        topK,
        threshold: 0.3,
        includeMetadata: true
      });

      return results;
    } catch (error) {
      console.error('Error searching content:', error);
      throw error;
    }
  }

  /**
   * Get vector store statistics
   */
  public getStats() {
    return this.vectorStore.getStatistics();
  }

  /**
   * Clear all stored content (for testing)
   */
  public clearAll() {
    this.vectorStore.clearAll();
  }
}
